import { useState, useEffect, useMemo } from 'react'
import { DATASETS, validateQuery, getMeasureMetadata } from '../lib/analyticalQuery'
import { VISUAL_TYPES } from '../lib/visualContract'
import { COMPOSITE_OUTPUTS } from '../lib/compositeOutputs'
import { executeAnalyticalOutput } from '../lib/queryDispatcher'
import { applyTopN } from '../lib/interactionState'
import { saveReport, updateReport, loadReport } from '../lib/savedReportsService'
import { exportAnalyticsReportPdf } from '../lib/exportAnalyticsReportPdf'
import { loadForecastVersions } from '../lib/buildCostControlData'
import DatasetCatalog from './DatasetCatalog'
import FilterPanel from './FilterPanel'
import KPI from './KPI'
import Table from './Table'
import Bar from './Bar'
import Line from './Line'
import Combo from './Combo'

// Which filter keys are worth offering per dataset — intersection of
// what the dataset's dimensions registry lists AND what
// financialSSOTAdapter.js/timeSeriesSSOTAdapter.js/
// monthlyActualCostSSOTAdapter.js actually implement filtering for
// (Stage 1).
const FILTERABLE_KEYS_BY_DATASET = {
  cost_performance: ['cost_code', 'cost_type', 'resource_type', 'indirect_category', 'sub_code', 'resource_code'],
  historical_comparison: [], // Cost Code grain only — no filter keys supported
  time_series: ['cost_code', 'cost_type', 'resource_type', 'indirect_category', 'sub_code', 'resource_code'],
  monthly_actual_cost: ['cost_code', 'resource_type', 'indirect_category', 'sub_code', 'resource_code'],
}

/**
 * ReportEditor — single component for New/Edit, per the approved
 * Phase 7B design, extended (Stage 8) with:
 *   - Composite Output selection (S-Curve) alongside the 4 Datasets
 *   - Multi-select FilterPanel (Stage 6)
 *   - Top N + Sort controls (applyTopN from interactionState.js — never
 *     persisted, applied to the preview result only)
 *   - Dataset Catalog (Stage 5) as the entry point for DATA selection
 *
 * Four-section layout: DATA (Catalog) / FILTERS (FilterPanel) /
 * ANALYSIS (measures, dimension, visual type, Top N) / REPORT (title,
 * notes, save) + a live PREVIEW panel.
 */
export default function ReportEditor({ projectId, reportId, onBack, onSaved }) {
  const [loadingExisting, setLoadingExisting] = useState(!!reportId)
  const [loadError, setLoadError] = useState(null)

  // ── DATA section ──────────────────────────────────────────────────────
  const [outputType, setOutputType] = useState('dataset') // 'dataset' | 'composite'
  const [datasetKey, setDatasetKey] = useState('cost_performance')
  const [compositeKey, setCompositeKey] = useState('s_curve')
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null) // historical_comparison only
  const [snapshotOptions, setSnapshotOptions] = useState([])

  // ── ANALYSIS section ─────────────────────────────────────────────────
  const [selectedMeasures, setSelectedMeasures] = useState([])
  const [dimension, setDimension] = useState(null)
  const [visualType, setVisualType] = useState('bar')
  const [topN, setTopN] = useState({ enabled: false, mode: 'top', n: 10, by: null })

  // ── FILTERS section ───────────────────────────────────────────────────
  const [filters, setFilters] = useState({})
  const [filterOptions, setFilterOptions] = useState({}) // populated from a real query result

  // ── REPORT section ────────────────────────────────────────────────────
  const [title, setTitle] = useState('')
  const [notes, setNotes] = useState('')

  const [previewResult, setPreviewResult] = useState(null)
  const [previewError, setPreviewError] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  const datasetDef = DATASETS[datasetKey]
  const isComposite = outputType === 'composite'

  // ── Load existing report (Edit mode) ─────────────────────────────────
  useEffect(() => {
    if (!reportId) return
    let cancelled = false
    async function load() {
      try {
        const saved = await loadReport(reportId)
        if (cancelled) return
        if (saved.analytical_definition.outputType === 'composite') {
          setOutputType('composite')
          setCompositeKey(saved.analytical_definition.compositeKey)
        } else {
          const q = saved.analytical_definition.query
          setOutputType('dataset')
          setDatasetKey(q.dataset)
          setSelectedMeasures(q.measures || [])
          setDimension(q.dimension ?? null)
          setFilters(q.filters || {})
        }
        setVisualType(saved.visualization.type)
        setTitle(saved.presentation.title || '')
        setNotes(saved.presentation.notes || '')
        setLoadingExisting(false)
      } catch (e) {
        if (!cancelled) { setLoadError(e.message); setLoadingExisting(false) }
      }
    }
    load()
    return () => { cancelled = true }
  }, [reportId])

  // ── Load snapshot options for historical_comparison ──────────────────
  useEffect(() => {
    if (isComposite || datasetKey !== 'historical_comparison') return
    let cancelled = false
    async function loadSnapshots() {
      const [direct, indirect] = await Promise.all([
        loadForecastVersions(projectId, 'direct'),
        loadForecastVersions(projectId, 'indirect'),
      ])
      if (cancelled) return
      const all = [...direct.revisions, ...indirect.revisions]
        .map(r => ({ id: r.id, label: `${r.forecast_date} (rev ${r.revision_number})` }))
      setSnapshotOptions(all)
      if (!selectedSnapshotId && all.length > 0) setSelectedSnapshotId(all[0].id)
    }
    loadSnapshots()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetKey, isComposite, projectId])

  // ── Load filter options for EVERY available filter key independently,
  //    not just the currently-selected dimension. Fixes a real design
  //    gap discovered after Stage 8: filterOptions was previously only
  //    populated as a side-effect of the main preview query (whichever
  //    dimension happened to be selected), so switching Dimension to
  //    'cost_code' left resource_type/sub_code/etc. filters permanently
  //    empty ("No matching options") even though real data exists for
  //    them. Each filter key now gets its own small, independent query —
  //    same executeAnalyticalOutput() path, just called once per key,
  //    at grain that key's own dimension (not the report's chosen one).
  useEffect(() => {
    if (isComposite) return
    let cancelled = false
    async function loadAllFilterOptions() {
      const keys = FILTERABLE_KEYS_BY_DATASET[datasetKey] || []
      const results = {}
      for (const key of keys) {
        // cost_type is a 2-value fixed set (direct/indirect) — no query
        // needed, same as it's already known from the Owner's own
        // documented values (buildCostControlData.js's directRows/
        // indirectRows split).
        if (key === 'cost_type') {
          results[key] = ['direct', 'indirect']
          continue
        }
        try {
          const optionResult = await executeAnalyticalOutput(projectId, {
            outputType: 'dataset',
            query: { dataset: datasetKey, measures: [datasetDef.measures[0]], dimension: key, comparison: null, time: null, filters: {} },
          }, selectedSnapshotId)
          if (cancelled) return
          if (optionResult.rows) {
            results[key] = optionResult.rows.map(row => row.key)
          } else if (optionResult.series) {
            // time_series/monthly_actual_cost return a series — options
            // are the union of keys across all points (a Sub Code may
            // not have a value in every period, but should still be
            // selectable).
            const allKeys = new Set()
            optionResult.series.forEach(point => point.rows.forEach(row => allKeys.add(row.key)))
            results[key] = [...allKeys]
          }
        } catch (e) {
          // A key this dataset's registry lists as filterable but that
          // fails to query independently (e.g. historical_comparison's
          // empty FILTERABLE_KEYS_BY_DATASET means this loop never
          // runs for it) — surfaced as an empty option list, not a
          // crash, since a missing filter option list should degrade
          // to "no options shown", not break the whole Editor.
          results[key] = []
        }
      }
      if (!cancelled) setFilterOptions(results)
    }
    loadAllFilterOptions()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetKey, isComposite, projectId, selectedSnapshotId])

  function handleSelectOutput(type, key) {
    setOutputType(type)
    if (type === 'dataset') {
      setDatasetKey(key)
      setSelectedMeasures([])
      setDimension(null)
      setFilters({})
    } else {
      setCompositeKey(key)
    }
    setPreviewResult(null)
    setPreviewError(null)
    setTopN({ enabled: false, mode: 'top', n: 10, by: null })
  }

  function toggleMeasure(m) {
    setSelectedMeasures(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])
    setPreviewResult(null)
    setPreviewError(null)
  }

  const draftQuery = useMemo(() => ({
    dataset: datasetKey,
    measures: selectedMeasures,
    dimension: dimension || null,
    comparison: null,
    time: null,
    filters,
  }), [datasetKey, selectedMeasures, dimension, filters])

  const draftAnalyticalDefinition = useMemo(() => {
    if (isComposite) {
      return { outputType: 'composite', compositeKey }
    }
    const def = { outputType: 'dataset', query: draftQuery }
    if (datasetKey === 'time_series') {
      def.temporalInput = { mode: 'all_available' }
    }
    return def
  }, [isComposite, compositeKey, draftQuery, datasetKey])

  const draftVisualization = useMemo(() => ({
    type: visualType,
    yAxis: isComposite ? COMPOSITE_OUTPUTS[compositeKey].fixedSeries : (selectedMeasures.length > 0 ? selectedMeasures : undefined),
  }), [visualType, isComposite, compositeKey, selectedMeasures])

  // ── Live Preview — debounced, applies Top N to the RESULT after execution ─
  useEffect(() => {
    if (!isComposite && selectedMeasures.length === 0) {
      setPreviewResult(null); setPreviewError(null); return
    }
    if (!isComposite) {
      const { valid, errors } = validateQuery(draftQuery)
      if (!valid) { setPreviewError(errors.join('; ')); setPreviewResult(null); return }
      if (datasetKey === 'historical_comparison' && !selectedSnapshotId) {
        setPreviewError('Select a snapshot to preview this dataset.'); setPreviewResult(null); return
      }
    }

    const timer = setTimeout(async () => {
      setPreviewLoading(true)
      setPreviewError(null)
      try {
        let result = await executeAnalyticalOutput(projectId, draftAnalyticalDefinition, selectedSnapshotId)
        if (topN.enabled && topN.by) {
          result = applyTopN(result, { mode: topN.mode, n: topN.n, by: topN.by })
        }
        setPreviewResult(result)
      } catch (e) {
        setPreviewError(e.message)
        setPreviewResult(null)
      } finally {
        setPreviewLoading(false)
      }
    }, 500)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(draftAnalyticalDefinition), selectedSnapshotId, projectId, JSON.stringify(topN)])

  async function handleSave() {
    setSaveError(null)
    if (!title.trim()) { setSaveError('Title is required.'); return }
    if (!isComposite && selectedMeasures.length === 0) { setSaveError('Select at least one measure.'); return }

    const reportToSave = {
      analytical_definition: draftAnalyticalDefinition,
      visualization: draftVisualization,
      presentation: { title: title.trim(), notes: notes.trim() || undefined },
    }

    setSaving(true)
    try {
      if (reportId) {
        await updateReport(reportId, reportToSave)
      } else {
        await saveReport(projectId, reportToSave, null)
      }
      setSaving(false)
      if (onSaved) onSaved()
    } catch (e) {
      setSaving(false)
      setSaveError(e.message)
    }
  }

  // Prints using the SAME previewResult already on screen — never
  // re-executes the query, per Stage 10's exportAnalyticsReportPdf.js
  // contract (printed numbers guaranteed to match what the person is
  // currently looking at).
  function handlePrint() {
    if (!previewResult) return
    const reportForPrint = {
      analytical_definition: draftAnalyticalDefinition,
      visualization: draftVisualization,
      presentation: { title: title.trim() || 'Untitled Report', notes: notes.trim() || undefined },
    }
    exportAnalyticsReportPdf(reportForPrint, previewResult, {})
  }

  if (loadingExisting) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading report…</div>
  }
  if (loadError) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#DC2626' }}>Failed to load report: {loadError}</div>
  }

  const availableFilterKeys = isComposite ? [] : (FILTERABLE_KEYS_BY_DATASET[datasetKey] || [])
  const measuresForTopN = isComposite ? COMPOSITE_OUTPUTS[compositeKey].fixedSeries : selectedMeasures

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto', fontFamily: 'system-ui' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800 }}>{reportId ? 'Edit Report' : 'New Report'}</h1>
        <button onClick={onBack} style={{ padding: '6px 14px', fontSize: 12, borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>← Back to Reports</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 240px 1fr', gap: 16 }}>
        {/* ── DATA ── */}
        <Panel title="Data">
          <DatasetCatalog
            selectedKey={isComposite ? compositeKey : datasetKey}
            selectedType={outputType}
            onSelect={handleSelectOutput}
          />
          {!isComposite && datasetKey === 'historical_comparison' && (
            <Field label="Snapshot">
              <select value={selectedSnapshotId || ''} onChange={e => setSelectedSnapshotId(e.target.value)} style={inputStyle}>
                <option value="">— Select a snapshot —</option>
                {snapshotOptions.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>
          )}
        </Panel>

        {/* ── FILTERS ── */}
        <Panel title="Filters">
          {isComposite ? (
            <div style={{ fontSize: 12, color: '#9CA3AF' }}>S-Curve has no filters — it always shows project-level totals.</div>
          ) : (
            <FilterPanel
              availableFilters={availableFilterKeys}
              optionsByKey={filterOptions}
              filters={filters}
              onChange={setFilters}
            />
          )}
        </Panel>

        {/* ── ANALYSIS + REPORT + PREVIEW ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Panel title="Analysis">
            {!isComposite && (
              <>
                <Field label="Measures">
                  {datasetDef.measures.map(m => (
                    <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginBottom: 4 }}>
                      <input type="checkbox" checked={selectedMeasures.includes(m)} onChange={() => toggleMeasure(m)} />
                      {getMeasureMetadata(m).label}
                    </label>
                  ))}
                </Field>

                <Field label="Dimension">
                  <select value={dimension || ''} onChange={e => { setDimension(e.target.value || null); setPreviewResult(null) }} style={inputStyle}>
                    <option value="">— None (project-level) —</option>
                    {datasetDef.dimensions.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
              </>
            )}

            <Field label="Visual Type">
              <select value={visualType} onChange={e => setVisualType(e.target.value)} style={inputStyle}>
                {VISUAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>

            {!isComposite && dimension && (
              <Field label="Top N">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
                  <input type="checkbox" checked={topN.enabled} onChange={e => setTopN(t => ({ ...t, enabled: e.target.checked }))} />
                  Limit to top/bottom N rows
                </label>
                {topN.enabled && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select value={topN.mode} onChange={e => setTopN(t => ({ ...t, mode: e.target.value }))} style={{ ...inputStyle, flex: 1 }}>
                      <option value="top">Top</option>
                      <option value="bottom">Bottom</option>
                    </select>
                    <select value={topN.n} onChange={e => setTopN(t => ({ ...t, n: Number(e.target.value) }))} style={{ ...inputStyle, flex: 1 }}>
                      {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <select value={topN.by || ''} onChange={e => setTopN(t => ({ ...t, by: e.target.value || null }))} style={{ ...inputStyle, flex: 1.5 }}>
                      <option value="">by…</option>
                      {measuresForTopN.map(m => <option key={m} value={m}>{getMeasureMetadata(m).label}</option>)}
                    </select>
                  </div>
                )}
              </Field>
            )}

            {saveError && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 8 }}>{saveError}</p>}
          </Panel>

          <Panel title="Report">
            <Field label="Title">
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Report title" style={inputStyle} />
            </Field>
            <Field label="Notes (optional)">
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Add context or commentary…" style={{ ...inputStyle, resize: 'vertical' }} />
            </Field>
            <button onClick={handleSave} disabled={saving}
              style={{ width: '100%', padding: '10px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : reportId ? 'Update Report' : 'Save Report'}
            </button>
            <button onClick={handlePrint} disabled={!previewResult}
              style={{ width: '100%', padding: '9px', marginTop: 8, background: '#fff', color: '#1B3A6B', border: '1px solid #1B3A6B', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: previewResult ? 'pointer' : 'not-allowed', opacity: previewResult ? 1 : 0.5 }}>
              🖨 Print / Export PDF
            </button>
          </Panel>

          <Panel title={`Live Preview ${previewLoading ? '(updating…)' : ''}`}>
            {previewError && (
              <div style={{ padding: 16, background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, fontSize: 12.5, color: '#991B1B' }}>
                {previewError}
              </div>
            )}
            {!previewError && !previewResult && (
              <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 12.5 }}>
                {isComposite ? 'Loading S-Curve…' : 'Select at least one measure to see a preview.'}
              </div>
            )}
            {!previewError && previewResult && (
              <VisualComponentRenderer
                type={visualType}
                result={previewResult}
                measures={measuresForTopN}
                isTimeSeries={isComposite || datasetKey === 'time_series'}
              />
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}

function VisualComponentRenderer({ type, result, measures, isTimeSeries }) {
  if (!measures || measures.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 12.5 }}>Select at least one measure to see a preview.</div>
  }

  if (type === 'combo' && !isTimeSeries) {
    return (
      <div style={{ padding: '16px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 12.5, color: '#92400E' }}>
        Combo requires the <strong>time_series</strong> dataset or the <strong>S-Curve</strong> composite output (it plots values across multiple points in time).
        For this dataset, use <strong>Table</strong> to compare measures side by side, or <strong>Bar</strong> for one measure at a time.
      </div>
    )
  }

  const singleMeasureTypes = ['kpi', 'bar']
  const showsOnlyFirst = singleMeasureTypes.includes(type) && measures.length > 1

  return (
    <div>
      {showsOnlyFirst && (
        <div style={{ padding: '8px 12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 6, fontSize: 11.5, color: '#92400E', marginBottom: 10 }}>
          {type === 'bar' ? 'Bar' : 'KPI'} shows one measure at a time — displaying <strong>{getMeasureMetadata(measures[0]).label}</strong> only.
          To compare {measures.length} measures together, use <strong>Table</strong> or <strong>Combo</strong> (time_series/S-Curve only).
        </div>
      )}
      {type === 'kpi' && <KPI result={result} measureKey={measures[0]} />}
      {type === 'bar' && <Bar result={result} measureKey={measures[0]} />}
      {type === 'table' && <Table result={result} yAxis={measures} />}
      {type === 'line' && <Line result={result} yAxis={measures} />}
      {type === 'combo' && <Combo result={result} yAxis={measures} />}
    </div>
  )
}

function Panel({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  )
}

const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit' }
